import axios from './index';

/** 短剧 / 演员徽章 */
export type DramaBadge = 'OFFICIAL' | 'PARTNER' | 'VERIFIED' | 'COMMUNITY';

/** 短剧 / 剧集 / 演员状态（与后端枚举一致） */
export type DramaStatus =
    | 'PENDING_REVIEW'
    | 'REVIEW_REJECTED'
    | 'REVIEW_APPROVED'
    | 'PENDING_ONLINE'
    | 'ONLINE'
    | 'OFFLINE'
    | 'DELETED'
    | string;

/** DreamOS review 事件类型 */
export type DramaReviewEventType = 'FIRST_PUBLISH' | 'ACTIVE_EDIT' | 'PASSIVE_EDIT' | string;

/** 管理员审核状态（列表 review.status） */
export type DramaReviewStatus =
    | 'Pending_FIRST_PUBLISH'
    | 'Pending_ACTIVE_EDIT'
    | 'Pending_PASSIVE_EDIT'
    | 'Approved'
    | 'Rejected'
    | 'Offline'
    /** @deprecated 旧值，兼容 */
    | 'Pending'
    | string;

/** @deprecated 使用 DramaStatus */
export type AuditStatus = DramaStatus;

export interface DramaActor {
    id: string;
    badge?: DramaBadge | null;
    name?: string;
    avatar?: string;
    avatar_url?: string;
    audit_status?: DramaStatus;
    status?: DramaStatus;
    audit_reason?: string | null;
    trust?: string | number;
    user_id?: string;
    created_at?: string | number | null;
    updated_at?: string | number | null;
    [property: string]: any;
}

/** 角色与演员的绑定关系 */
export interface DramaCastRel {
    id?: string;
    actor_id?: string;
    drama_id?: string;
    role_id?: string;
    actor?: DramaActor;
    [property: string]: any;
}

/** 角色（列表 roles）；演员绑定在 castRels */
export interface DramaRole {
    id?: string;
    name?: string;
    role_name?: string;
    avatar_url?: string;
    bio?: string;
    sort_no?: number;
    drama_id?: string;
    actors?: DramaActor[];
    castRels?: DramaCastRel[];
    [property: string]: any;
}

export interface DramaEpisode {
    id: string;
    drama_id?: string;
    title?: string;
    name?: string;
    description?: string;
    episode_no?: number | string;
    episode_number?: number;
    sort?: number;
    video?: string;
    video_url?: string;
    hls_url?: string;
    cover?: string;
    duration_sec?: number | string;
    play_count?: number | string;
    complete_count?: number | string;
    like_count?: number | string;
    comment_count?: number | string;
    created_at?: string | number | null;
    updated_at?: string | number | null;
    [property: string]: any;
}

/** 列表返回的 DreamOS review 摘要 */
export interface DramaReviewInfo {
    id?: string;
    drama_id?: string;
    user_id?: string;
    title?: string;
    status?: DramaReviewStatus;
    last_event_type?: DramaReviewEventType;
    version?: number;
    created_at?: string | number | null;
    updated_at?: string | number | null;
    [property: string]: any;
}

export interface DramaHistoryReviewerAccount {
    id?: string;
    account?: string;
}

export interface DramaHistoryItem {
    id?: string;
    drama_review_id?: string;
    status?: DramaReviewStatus | string;
    reviewer_account_id?: string;
    reviewer_account?: DramaHistoryReviewerAccount | null;
    reason?: string | null;
    created_at?: string | number | null;
    [property: string]: any;
}

export interface DramaItem {
    id: string;
    badge?: DramaBadge | null;
    user_id?: string;
    title?: string;
    cover_url?: string;
    description?: string | null;
    status?: DramaStatus;
    audit_reason?: string | null;
    online_at?: string | number | null;
    offline_at?: string | number | null;
    created_at?: string | number | null;
    updated_at?: string | number | null;
    roles?: DramaRole[];
    /** 真实列表字段 */
    dramaEpisodes?: DramaEpisode[];
    /** 兼容旧字段 */
    episodes?: DramaEpisode[];
    history?: DramaHistoryItem[];
    review?: DramaReviewInfo | null;
    /** 当前热度系数（乘在 heat_raw_original 上的乘数） */
    heat_coefficient?: number | null;
    /** 当前热度系数最近修改时间 */
    heat_coefficient_updated_at?: string | number | null;
    /** 兼容旧字段 */
    name?: string;
    cover?: string;
    video?: string;
    video_url?: string;
    audit_status?: DramaStatus;
    [property: string]: any;
}

export interface DramaReviewPayload {
    approved: boolean;
    auditReason: string;
}

/** 短剧下架 */
export interface DramaOfflinePayload {
    reason: string;
}

/** 设置短剧热度系数（0 ~ 100000，最多 2 位小数） */
export interface DramaHeatCoefficientPayload {
    heat_coefficient: number;
}

/** 列表 sort：sort[0][field]=asc|desc */
export type DramaListSortField = 'created_at' | 'updated_at' | 'online_at';
export type DramaListSortDirection = 'asc' | 'desc';
export type DramaListSortItem = Partial<Record<DramaListSortField, DramaListSortDirection>>;
export type DramaListSort = DramaListSortItem[];

/** 列表查询 status（管理员审核状态） */
export type DramaListStatusFilter =
    | 'Pending_FIRST_PUBLISH'
    | 'Pending_ACTIVE_EDIT'
    | 'Pending_PASSIVE_EDIT'
    | 'Approved'
    | 'Rejected'
    | 'Offline'
    | string;

/** 列表查询 drama_status（短剧状态） */
export type DramaListDramaStatusFilter =
    | 'ONLINE'
    | 'DELETED'
    | 'PENDING_REVIEW'
    | 'OFFLINE'
    | 'REVIEW_REJECTED'
    | string;

export interface DramaListParams {
    page?: number | string;
    size?: number | string;
    /** 精确匹配短剧 ID */
    id?: string;
    /** 短剧标题模糊搜索 */
    title_fuzzy?: string;
    /** 描述模糊搜索（短视频列表） */
    description_fuzzy?: string;
    /** 创建人 UID */
    user_id?: string;
    /** 上线时间：[开始时间, 结束时间] */
    online_at?: [string, string];
    /** Pending_FIRST_PUBLISH / Pending_ACTIVE_EDIT / Pending_PASSIVE_EDIT / Approved / Rejected / Offline */
    status?: DramaListStatusFilter;
    /** ONLINE / DELETED / PENDING_REVIEW / OFFLINE / REVIEW_REJECTED */
    drama_status?: DramaListDramaStatusFilter;
    sort?: DramaListSort;
    [property: string]: any;
}

export interface DramaPageMeta {
    cursor?: string;
    more?: boolean;
    page?: number | null;
    size?: number;
    total?: number;
    [property: string]: any;
}

export interface ApiResponse<T = unknown> {
    code: number;
    data?: T;
    errors?: any;
    meta?: DramaPageMeta | null;
    msg: string;
    [property: string]: any;
}

export const dramaList = (params?: DramaListParams, config?: { signal?: AbortSignal }) =>
    axios<ApiResponse<DramaItem[]>>('/v1/dramas', {
        method: 'get',
        params,
        paramsSerializer: {
            indexes: true,
        },
        signal: config?.signal,
    });

export const dramaReview = (dramaId: string, data: DramaReviewPayload) =>
    axios<ApiResponse>(`/v1/dramas/${dramaId}/review`, {
        method: 'post',
        data,
    });

/** 短剧下架：前置 ONLINE / PENDING_REVIEW → OFFLINE */
export const dramaOffline = (dramaId: string, data: DramaOfflinePayload) =>
    axios<ApiResponse>(`/v1/dramas/${dramaId}/offline`, {
        method: 'post',
        data,
    });

export const updateDramaBadge = (dramaId: string, data: { badge: DramaBadge }) =>
    axios<ApiResponse>(`/v1/dramas/${dramaId}/badge`, {
        method: 'post',
        data,
    });

/** 设置短剧热度系数；成功后需重新拉列表展示新系数 */
export const updateDramaHeatCoefficient = (dramaId: string, data: DramaHeatCoefficientPayload) =>
    axios<ApiResponse>(`/v1/dramas/${dramaId}/heat-coefficient`, {
        method: 'post',
        data,
    });

/** 导出短剧：参数与列表查询一致 */
export const dramaExport = (params?: DramaListParams) =>
    axios<ApiResponse>('/v1/dramas/export', {
        method: 'get',
        params,
        paramsSerializer: {
            indexes: true,
        },
    });
